import { z } from 'zod';

export const AccountSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  authProviderId: z.string().optional(),
  verifiedAt: z.string().nullable().optional(),
  isEmailVerified: z.boolean().optional(),
  createdAt: z.string().optional(),
});

export const AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresIn: z.number().optional(),
});

export const PerkInfoSchema = z
  .object({
    perkKey: z.string(),
  })
  .passthrough();

export const PlatformProfileSchema = z
  .object({
    id: z.string().optional(),
    userId: z.string().optional(),
    name: z.string().optional(),
    scopeId: z.string().optional(),
    perks: z.array(PerkInfoSchema).optional(),
  })
  .passthrough();

export const SignInResponseSchema = z.object({
  tokens: AuthTokensSchema.optional(),
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
  account: AccountSchema.nullable().optional(),
  user: AccountSchema.nullable().optional(),
  platformProfile: PlatformProfileSchema.nullable().optional(),
});

export type Account = z.infer<typeof AccountSchema>;
export type AuthTokens = z.infer<typeof AuthTokensSchema>;
export type SignInResponse = z.infer<typeof SignInResponseSchema>;

export interface SignInInput {
  email: string;
  password: string;
}

export interface ForgotPasswordInput {
  email: string;
}

export interface ResetPasswordInput {
  token: string;
  password: string;
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export const StoredAuthSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  accountId: z.string(),
  email: z.string(),
  /** Perk keys do platform profile no scope atual — usados como papéis (ex.: adminDashboard.administrator). */
  roles: z.array(z.string()).optional(),
});

export type StoredAuth = z.infer<typeof StoredAuthSchema>;

/**
 * Extrai os papéis (perk keys) da resposta de sign-in. O backend resolve os
 * perks do platform profile do usuário no scope; aqui apenas os coletamos para
 * UX (esconder menu / redirecionar). A autorização real é feita no servidor.
 */
export function extractRoles(data: SignInResponse): string[] {
  return data.platformProfile?.perks?.map((perk) => perk.perkKey) ?? [];
}

/**
 * Resposta de `GET /auth/me/perks` — revalidação sob demanda das roles do
 * usuário logado no scope atual (sem refazer o sign-in inteiro). Usada pelo
 * `AuthGuard` pra refletir um perk concedido em runtime (ex.: painel de
 * Vantagens) sem exigir logout/login.
 */
export const MyPerksResponseSchema = z.object({
  perks: z.array(PerkInfoSchema),
});

export type MyPerksResponse = z.infer<typeof MyPerksResponseSchema>;

export function extractPerkKeys(data: MyPerksResponse): string[] {
  return data.perks.map((perk) => perk.perkKey);
}
